/**
 * WhatsApp Floating Button
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE fixed button. Optional chat popup (name + email + message → WhatsApp).
 * All FX are opt-in via config flags.
 *
 * Auto-inits on page load. Set window.WHATSAPP_BTN_CONFIG before loading.
 *
 * FX options:
 *   fx.ripple      – ink-ripple on click
 *   fx.pulse       – glowing animated ring around button
 *   fx.bounceIn    – button slides+bounces in from the screen edge on load
 *   fx.tooltip     – label tooltip on hover
 *   fx.badge       – red notification dot (with optional count)
 */

// ─── LANGUAGE PACKS ──────────────────────────────────────────────────────────
const WHATSAPP_BTN_LANGS = {
  en: {
    labelText:          "Chat with us",
    messagePrefix:      "Hi! I found your page: ",
    chatTitle:          "Chat with us",
    chatSubtitle:       "We reply within minutes.",
    namePlaceholder:    "Your name *",
    emailPlaceholder:   "Your email *",
    messagePlaceholder: "Message (optional)",
    submitText:         "Send on WhatsApp",
    ariaLabel:          "Chat on WhatsApp",
    errName:            "Please enter your name.",
    errEmail:           "Please enter a valid email.",
    greeting:           "Hi! I'm",
    pageLine:           "Page:",
  },
  fr: {
    labelText:          "Discuter avec nous",
    messagePrefix:      "Bonjour ! J'ai trouvé votre page : ",
    chatTitle:          "Discuter avec nous",
    chatSubtitle:       "Nous répondons en quelques minutes.",
    namePlaceholder:    "Votre nom *",
    emailPlaceholder:   "Votre email *",
    messagePlaceholder: "Message (facultatif)",
    submitText:         "Envoyer sur WhatsApp",
    ariaLabel:          "Discuter sur WhatsApp",
    errName:            "Veuillez entrer votre nom.",
    errEmail:           "Veuillez entrer un email valide.",
    greeting:           "Bonjour ! Je suis",
    pageLine:           "Page :",
  },
  es: {
    labelText:          "Chatea con nosotros",
    messagePrefix:      "¡Hola! Encontré tu página: ",
    chatTitle:          "Chatea con nosotros",
    chatSubtitle:       "Respondemos en minutos.",
    namePlaceholder:    "Tu nombre *",
    emailPlaceholder:   "Tu email *",
    messagePlaceholder: "Mensaje (opcional)",
    submitText:         "Enviar por WhatsApp",
    ariaLabel:          "Chatear en WhatsApp",
    errName:            "Por favor ingresa tu nombre.",
    errEmail:           "Por favor ingresa un email válido.",
    greeting:           "¡Hola! Soy",
    pageLine:           "Página:",
  },
  ar: {
    labelText:          "تحدث معنا",
    messagePrefix:      "مرحباً! وجدت صفحتكم: ",
    chatTitle:          "تحدث معنا",
    chatSubtitle:       "نرد خلال دقائق.",
    namePlaceholder:    "* اسمك",
    emailPlaceholder:   "* بريدك الإلكتروني",
    messagePlaceholder: "(اختياري) رسالة",
    submitText:         "أرسل عبر واتساب",
    ariaLabel:          "الدردشة عبر واتساب",
    errName:            "يرجى إدخال اسمك.",
    errEmail:           "يرجى إدخال بريد إلكتروني صالح.",
    greeting:           "مرحباً! أنا",
    pageLine:           "الصفحة:",
  },
  de: {
    labelText:          "Chatte mit uns",
    messagePrefix:      "Hallo! Ich habe Ihre Seite gefunden: ",
    chatTitle:          "Chatte mit uns",
    chatSubtitle:       "Wir antworten innerhalb von Minuten.",
    namePlaceholder:    "Ihr Name *",
    emailPlaceholder:   "Ihre E-Mail *",
    messagePlaceholder: "Nachricht (optional)",
    submitText:         "Über WhatsApp senden",
    ariaLabel:          "Auf WhatsApp chatten",
    errName:            "Bitte geben Sie Ihren Namen ein.",
    errEmail:           "Bitte geben Sie eine gültige E-Mail ein.",
    greeting:           "Hallo! Ich bin",
    pageLine:           "Seite:",
  },
  pt: {
    labelText:          "Fale conosco",
    messagePrefix:      "Olá! Encontrei sua página: ",
    chatTitle:          "Fale conosco",
    chatSubtitle:       "Respondemos em minutos.",
    namePlaceholder:    "Seu nome *",
    emailPlaceholder:   "Seu email *",
    messagePlaceholder: "Mensagem (opcional)",
    submitText:         "Enviar no WhatsApp",
    ariaLabel:          "Conversar no WhatsApp",
    errName:            "Por favor, insira seu nome.",
    errEmail:           "Por favor, insira um email válido.",
    greeting:           "Olá! Eu sou",
    pageLine:           "Página:",
  },
};

// ─── DEFAULT CONFIG ───────────────────────────────────────────────────────────
const WHATSAPP_BTN_CONFIG = {
  // ── Core ──────────────────────────────────────────────────────────────────
  phone:         "1234567890",       // international format, no + or spaces
  messagePrefix: "",                 // auto-filled from lang if empty

  // ── Language ─────────────────────────────────────────────────────────────
  // "en" | "fr" | "es" | "ar" | "de" | "pt"  — or pass a custom object
  lang: "en",

  // ── Layout ────────────────────────────────────────────────────────────────
  position:       "bottom-right",    // bottom-right | bottom-left | top-right | top-left
  shape:          "circle",          // circle | rect
  template:       "template1",       // template1 (green) | template2 (dark)
  flipHorizontal: false,             // mirror icon
  labelSide:      "right",           // left | right  (rect + tooltip)
  showLabel:      false,             // inline label (rect only)
  labelText:      "",                // auto-filled from lang if empty
  size:           56,                // px  (circle = diameter, rect = height)
  offset:         24,                // px from screen edge (shorthand — sets both X & Y)
  offsetX:        null,              // px from left/right edge (overrides offset)
  offsetY:        null,              // px from top/bottom edge (overrides offset)
  zIndex:         9999,
  newTab:         true,

  // ── Chat popup ────────────────────────────────────────────────────────────
  chat: {
    enabled:      false,             // true → clicking opens popup instead of WhatsApp directly
    title:        "",                // auto-filled from lang if empty
    subtitle:     "",
    namePlaceholder:    "",
    emailPlaceholder:   "",
    messagePlaceholder: "",
    submitText:   "",
  },

  // ── FX (all optional, all false by default) ────────────────────────────────
  fx: {
    ripple:   false,   // ink ripple on click
    pulse:    false,   // glowing ring animation
    bounceIn: false,   // slide+bounce entrance
    tooltip:  false,   // hover tooltip
    badge:    false,   // red notification dot
    badgeCount: "",    // "" | "1" | "99+" etc.
  },
};

// ─── TEMPLATES ────────────────────────────────────────────────────────────────
const WHATSAPP_BTN_STYLES = {
  template1: {
    bg:           "#25D366",
    bgHover:      "#1DA851",
    color:        "#fff",
    shadow:       "0 4px 16px rgba(37,211,102,0.5)",
    shadowHover:  "0 6px 24px rgba(37,211,102,0.7)",
    pulse:        "rgba(37,211,102,0.4)",
    radius_circle:"50%",
    radius_rect:  "100px",
    font:         "system-ui,-apple-system,sans-serif",
    // chat popup
    chatAccent:   "#25D366",
    chatBtn:      "#25D366",
    chatBtnText:  "#fff",
  },
  template2: {
    bg:           "#1a1a2e",
    bgHover:      "#16213e",
    color:        "#25D366",
    shadow:       "0 4px 16px rgba(0,0,0,0.45)",
    shadowHover:  "0 6px 24px rgba(0,0,0,0.65)",
    pulse:        "rgba(37,211,102,0.3)",
    radius_circle:"50%",
    radius_rect:  "100px",
    font:         "system-ui,-apple-system,sans-serif",
    chatAccent:   "#25D366",
    chatBtn:      "#25D366",
    chatBtnText:  "#fff",
  },
};

// ─── SVG ──────────────────────────────────────────────────────────────────────
function _waSVG(color, size, flip) {
  const s = Math.round(size * 0.52);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 24 24" fill="${color}" style="display:block;flex-shrink:0;${flip?"transform:scaleX(-1);":""}">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>`;
}

function _waCloseSVG(color, size) {
  const s = Math.round(size * 0.45);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" style="display:block;">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>`;
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
function _waInjectCSS() {
  if (document.getElementById("wa-btn-css")) return;
  const css = `
    /* ── button ── */
    #wa-float-btn {
      position:fixed; display:inline-flex; align-items:center; justify-content:center;
      cursor:pointer; text-decoration:none; user-select:none; overflow:visible;
      transition:background .2s, box-shadow .2s, transform .15s;
      white-space:nowrap;
    }
    #wa-float-btn:active { transform:scale(0.93) !important; }

    /* ── ripple wrapper (clips ripple while button overflows for badge) ── */
    .wa-btn-inner {
      position:absolute; inset:0; overflow:hidden;
      border-radius:inherit; pointer-events:none;
    }

    /* ── ripple ── */
    .wa-ripple-circle {
      position:absolute; border-radius:50%; transform:scale(0);
      background:rgba(255,255,255,0.45); pointer-events:none;
      animation:wa-ripple-anim .55s linear forwards;
    }
    @keyframes wa-ripple-anim {
      to { transform:scale(4); opacity:0; }
    }

    /* ── pulse ring ── */
    #wa-pulse-ring {
      position:fixed; border-radius:50%; pointer-events:none;
      animation:wa-pulse 2s ease-out infinite;
    }
    @keyframes wa-pulse {
      0%   { transform:scale(1);    opacity:.8; }
      70%  { transform:scale(1.55); opacity:0;  }
      100% { transform:scale(1.55); opacity:0;  }
    }

    /* ── bounce-in ── */
    @keyframes wa-bounce-in-right {
      0%   { transform:translateX(120px); opacity:0; }
      60%  { transform:translateX(-10px); opacity:1; }
      80%  { transform:translateX(6px);              }
      100% { transform:translateX(0);                }
    }
    @keyframes wa-bounce-in-left {
      0%   { transform:translateX(-120px); opacity:0; }
      60%  { transform:translateX(10px);   opacity:1; }
      80%  { transform:translateX(-6px);              }
      100% { transform:translateX(0);                 }
    }
    @keyframes wa-bounce-in-bottom {
      0%   { transform:translateY(100px); opacity:0; }
      60%  { transform:translateY(-8px);  opacity:1; }
      80%  { transform:translateY(4px);              }
      100% { transform:translateY(0);                }
    }
    @keyframes wa-bounce-in-top {
      0%   { transform:translateY(-100px); opacity:0; }
      60%  { transform:translateY(8px);    opacity:1; }
      80%  { transform:translateY(-4px);              }
      100% { transform:translateY(0);                 }
    }

    /* ── tooltip ── */
    #wa-tooltip {
      position:fixed; pointer-events:none; white-space:nowrap;
      background:#1a1a1a; color:#fff; font-size:12px; font-weight:600;
      padding:5px 10px; border-radius:6px; opacity:0;
      transition:opacity .18s; z-index:10000;
    }
    #wa-tooltip::after {
      content:""; position:absolute; top:50%; border:5px solid transparent;
    }
    #wa-tooltip.tt-left::after  { right:-9px; transform:translateY(-50%); border-left-color:#1a1a1a; }
    #wa-tooltip.tt-right::after { left:-9px;  transform:translateY(-50%); border-right-color:#1a1a1a; }

    /* ── badge (child of button, position:absolute) ── */
    #wa-badge {
      position:absolute; top:-6px; right:-6px;
      background:#e53e3e; color:#fff;
      border-radius:100px; font-size:10px; font-weight:700;
      min-width:18px; height:18px; display:flex; align-items:center;
      justify-content:center; padding:0 4px; pointer-events:none;
      border:2px solid #fff; z-index:1;
      animation:wa-badge-pop .3s cubic-bezier(.34,1.56,.64,1) both;
    }
    @keyframes wa-badge-pop {
      0%   { transform:scale(0); }
      100% { transform:scale(1); }
    }

    /* ── chat popup ── */
    #wa-chat-popup {
      position:fixed; z-index:10002;
      width:320px; border-radius:16px; overflow:hidden;
      box-shadow:0 8px 40px rgba(0,0,0,0.18);
      transform:scale(0.85) translateY(20px); opacity:0; pointer-events:none;
      transition:transform .25s cubic-bezier(.34,1.56,.64,1), opacity .2s;
      transform-origin:bottom center;
    }
    #wa-chat-popup.wa-open {
      transform:scale(1) translateY(0); opacity:1; pointer-events:all;
    }
    .wa-chat-header {
      display:flex; align-items:center; gap:12px; padding:16px 18px;
    }
    .wa-chat-avatar {
      width:42px; height:42px; border-radius:50%; background:rgba(255,255,255,0.2);
      display:flex; align-items:center; justify-content:center; flex-shrink:0;
    }
    .wa-chat-header-text { flex:1; min-width:0; }
    .wa-chat-title   { font-size:15px; font-weight:700; margin:0 0 2px; color:#fff; }
    .wa-chat-subtitle{ font-size:12px; color:rgba(255,255,255,0.8); margin:0; }
    .wa-chat-close {
      background:rgba(255,255,255,0.15); border:none; border-radius:50%;
      width:30px; height:30px; display:flex; align-items:center; justify-content:center;
      cursor:pointer; flex-shrink:0; transition:background .15s;
    }
    .wa-chat-close:hover { background:rgba(255,255,255,0.28); }
    .wa-chat-body { background:#fff; padding:20px; }
    .wa-chat-body input,
    .wa-chat-body textarea {
      width:100%; border:1.5px solid #e2e8f0; border-radius:8px;
      padding:10px 12px; font-size:14px; font-family:inherit;
      outline:none; transition:border .15s; resize:none;
      box-sizing:border-box; margin-bottom:10px; display:block;
    }
    .wa-chat-body input:focus,
    .wa-chat-body textarea:focus { border-color:#25D366; }
    .wa-chat-body input.wa-error,
    .wa-chat-body textarea.wa-error { border-color:#e53e3e; }
    .wa-chat-submit {
      width:100%; border:none; border-radius:8px; padding:12px;
      font-size:14px; font-weight:700; cursor:pointer; display:flex;
      align-items:center; justify-content:center; gap:8px;
      transition:opacity .15s, transform .12s;
    }
    .wa-chat-submit:hover  { opacity:.88; }
    .wa-chat-submit:active { transform:scale(.97); }
    .wa-chat-error { color:#e53e3e; font-size:12px; margin:-6px 0 8px; display:none; }
  `;
  const tag = document.createElement("style");
  tag.id = "wa-btn-css";
  tag.textContent = css;
  document.head.appendChild(tag);
}

// ─── MAIN CLASS ───────────────────────────────────────────────────────────────
class WhatsAppButton {
  constructor(userConfig = {}) {
    // deep-merge fx and chat sub-objects
    const base = JSON.parse(JSON.stringify(WHATSAPP_BTN_CONFIG));
    const win  = window.WHATSAPP_BTN_CONFIG || {};

    this.config = Object.assign(base, win, userConfig);
    this.config.fx   = Object.assign(base.fx,   win.fx   || {}, userConfig.fx   || {});
    this.config.chat = Object.assign(base.chat,  win.chat || {}, userConfig.chat || {});

    // resolve language: string key → lookup, or custom object
    const langKey = this.config.lang;
    const langPack = typeof langKey === "object" ? langKey
                   : (WHATSAPP_BTN_LANGS[langKey] || WHATSAPP_BTN_LANGS.en);
    this._lang = langPack;

    // fill empty strings from language pack
    const cfg = this.config;
    if (!cfg.messagePrefix) cfg.messagePrefix = langPack.messagePrefix;
    if (!cfg.labelText)     cfg.labelText     = langPack.labelText;

    const c = cfg.chat;
    if (!c.title)              c.title              = langPack.chatTitle;
    if (!c.subtitle)           c.subtitle           = langPack.chatSubtitle;
    if (!c.namePlaceholder)    c.namePlaceholder    = langPack.namePlaceholder;
    if (!c.emailPlaceholder)   c.emailPlaceholder   = langPack.emailPlaceholder;
    if (!c.messagePlaceholder) c.messagePlaceholder = langPack.messagePlaceholder;
    if (!c.submitText)         c.submitText         = langPack.submitText;

    this._btn      = null;
    this._pulse    = null;
    this._tooltip  = null;
    this._badge    = null;
    this._popup    = null;
    this._popupOpen = false;
  }

  // ── helpers ──────────────────────────────────────────────────────────────
  _tmpl()      { return WHATSAPP_BTN_STYLES[this.config.template] || WHATSAPP_BTN_STYLES.template1; }
  _isRight()   { return this.config.position.includes("right"); }
  _isBottom()  { return this.config.position.includes("bottom"); }

  _buildURL(extra = "") {
    const page = document.title || window.location.href;
    const text = encodeURIComponent(this.config.messagePrefix + page + extra);
    return `https://wa.me/${this.config.phone}?text=${text}`;
  }

  _posCSS() {
    const cfg = this.config;
    const x = `${cfg.offsetX != null ? cfg.offsetX : cfg.offset}px`;
    const y = `${cfg.offsetY != null ? cfg.offsetY : cfg.offset}px`;
    const map = {
      "bottom-right": `bottom:${y};right:${x};`,
      "bottom-left":  `bottom:${y};left:${x};`,
      "top-right":    `top:${y};right:${x};`,
      "top-left":     `top:${y};left:${x};`,
    };
    return map[cfg.position] || map["bottom-right"];
  }

  // ── BUTTON ───────────────────────────────────────────────────────────────
  _createBtn() {
    const cfg  = this.config;
    const t    = this._tmpl();
    const isC  = cfg.shape === "circle";

    const btn = document.createElement("a");
    btn.id    = "wa-float-btn";
    btn.setAttribute("aria-label", this._lang.ariaLabel);

    if (!cfg.chat.enabled) {
      btn.href   = this._buildURL();
      btn.target = cfg.newTab ? "_blank" : "_self";
      btn.rel    = "noopener noreferrer";
    } else {
      btn.href = "#";
      btn.setAttribute("role", "button");
    }

    btn.style.cssText = [
      this._posCSS(),
      `z-index:${cfg.zIndex}`,
      `background:${t.bg}`,
      `color:${t.color}`,
      `border-radius:${isC ? t.radius_circle : t.radius_rect}`,
      isC ? `width:${cfg.size}px;height:${cfg.size}px;padding:0;`
          : `height:${cfg.size}px;padding:0 20px;`,
      `box-shadow:${t.shadow}`,
      `font-family:${t.font}`,
      "font-size:14px;font-weight:600;letter-spacing:.01em;",
      `flex-direction:${cfg.labelSide === "left" ? "row-reverse" : "row"}`,
      "gap:10px",
    ].join(";");

    // hover
    btn.addEventListener("mouseenter", () => {
      btn.style.background = t.bgHover;
      btn.style.boxShadow  = t.shadowHover;
      btn.style.transform  = "scale(1.06)";
    });
    btn.addEventListener("mouseleave", () => {
      btn.style.background = t.bg;
      btn.style.boxShadow  = t.shadow;
      btn.style.transform  = "scale(1)";
    });

    // icon
    const icon = document.createElement("span");
    icon.style.cssText = "display:flex;align-items:center;justify-content:center;flex-shrink:0;";
    icon.innerHTML     = _waSVG(t.color, cfg.size, cfg.flipHorizontal);
    btn.appendChild(icon);

    // inline label (rect only)
    if (cfg.showLabel && !isC) {
      const lbl = document.createElement("span");
      lbl.textContent = cfg.labelText;
      lbl.style.color = t.color;
      btn.appendChild(lbl);
    }

    // ── FX: ripple (uses .wa-btn-inner wrapper to clip while badge overflows) ──
    if (cfg.fx.ripple) {
      const ripWrap = document.createElement("span");
      ripWrap.className = "wa-btn-inner";
      btn.appendChild(ripWrap);
      btn.addEventListener("click", (e) => {
        const r   = btn.getBoundingClientRect();
        const sz  = Math.max(r.width, r.height) * 2;
        const rip = document.createElement("span");
        rip.className  = "wa-ripple-circle";
        rip.style.cssText = `width:${sz}px;height:${sz}px;
          left:${e.clientX - r.left - sz / 2}px;
          top:${e.clientY  - r.top  - sz / 2}px;`;
        ripWrap.appendChild(rip);
        rip.addEventListener("animationend", () => rip.remove());
      });
    }

    // ── FX: bounceIn ─────────────────────────────────────────────────────
    if (cfg.fx.bounceIn) {
      const animName = this._isRight() ? "wa-bounce-in-right"
                     : this._isBottom() ? "wa-bounce-in-bottom"
                     : this._isRight()  ? "wa-bounce-in-right"
                     : "wa-bounce-in-left";
      const dir = this._isRight() ? "wa-bounce-in-right" : "wa-bounce-in-left";
      btn.style.animation = `${dir} .65s cubic-bezier(.22,.68,0,1.2) both`;
    }

    // ── chat popup toggle ─────────────────────────────────────────────────
    if (cfg.chat.enabled) {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this._togglePopup();
      });
    } else {
      // update href on click (SPA title may have changed)
      btn.addEventListener("click", () => { btn.href = this._buildURL(); });
    }

    this._btn = btn;
    return btn;
  }

  // ── FX: pulse ring ───────────────────────────────────────────────────────
  _createPulse() {
    const cfg = this.config;
    const t   = this._tmpl();
    const el  = document.createElement("div");
    el.id     = "wa-pulse-ring";
    el.style.cssText = [
      this._posCSS(),
      `z-index:${cfg.zIndex - 1}`,
      `width:${cfg.size}px;height:${cfg.size}px;`,
      `background:${t.pulse}`,
    ].join(";");
    this._pulse = el;
    return el;
  }

  // ── FX: tooltip ──────────────────────────────────────────────────────────
  _createTooltip() {
    const cfg = this.config;
    const el  = document.createElement("div");
    el.id     = "wa-tooltip";
    el.textContent = cfg.labelText || "Chat with us";
    el.className   = this._isRight() ? "tt-left" : "tt-right";

    const show = () => {
      const r  = this._btn.getBoundingClientRect();
      const gap = 12;
      el.style.top = `${r.top + r.height / 2 - 14}px`;
      if (this._isRight()) {
        el.style.right = `${window.innerWidth - r.left + gap}px`;
        el.style.left  = "auto";
      } else {
        el.style.left  = `${r.right + gap}px`;
        el.style.right = "auto";
      }
      el.style.opacity = "1";
    };
    const hide = () => { el.style.opacity = "0"; };

    this._btn.addEventListener("mouseenter", show);
    this._btn.addEventListener("mouseleave", hide);
    this._tooltip = el;
    return el;
  }

  // ── FX: badge (child of button — CSS handles placement) ─────────────────
  _createBadge() {
    const cfg = this.config;
    const el  = document.createElement("div");
    el.id     = "wa-badge";
    el.textContent = cfg.fx.badgeCount || "";
    // badge is position:absolute inside the button — CSS handles placement
    this._badge = el;
    return el;
  }

  // ── CHAT POPUP ───────────────────────────────────────────────────────────
  _createPopup() {
    const cfg = this.config;
    const t   = this._tmpl();
    const c   = cfg.chat;
    const isR = this._isRight();
    const isB = this._isBottom();
    const oX = cfg.offsetX != null ? cfg.offsetX : cfg.offset;
    const oY = cfg.offsetY != null ? cfg.offsetY : cfg.offset;

    const popup = document.createElement("div");
    popup.id = "wa-chat-popup";

    // position above/below button
    if (isB) {
      popup.style.bottom = `${oY + cfg.size + 12}px`;
    } else {
      popup.style.top = `${oY + cfg.size + 12}px`;
    }
    if (isR) {
      popup.style.right = `${oX}px`;
    } else {
      popup.style.left = `${oX}px`;
    }
    popup.style.transformOrigin = isB ? "bottom center" : "top center";
    popup.style.zIndex = String(cfg.zIndex + 1);

    popup.innerHTML = `
      <div class="wa-chat-header" style="background:${t.bg};">
        <div class="wa-chat-avatar">
          ${_waSVG("#fff", 36, false)}
        </div>
        <div class="wa-chat-header-text">
          <p class="wa-chat-title">${c.title}</p>
          <p class="wa-chat-subtitle">${c.subtitle}</p>
        </div>
        <button class="wa-chat-close" id="wa-chat-close-btn" aria-label="Close">
          ${_waCloseSVG("#fff", 30)}
        </button>
      </div>
      <div class="wa-chat-body">
        <input  type="text"  id="wa-chat-name"    placeholder="${c.namePlaceholder}"    autocomplete="name" />
        <p class="wa-chat-error" id="wa-err-name">${this._lang.errName}</p>
        <input  type="email" id="wa-chat-email"   placeholder="${c.emailPlaceholder}"   autocomplete="email" />
        <p class="wa-chat-error" id="wa-err-email">${this._lang.errEmail}</p>
        <textarea id="wa-chat-msg" rows="3" placeholder="${c.messagePlaceholder}"></textarea>
        <button class="wa-chat-submit" id="wa-chat-submit"
          style="background:${t.chatBtn};color:${t.chatBtnText};">
          ${_waSVG(t.chatBtnText, 34, false)}
          ${c.submitText}
        </button>
      </div>
    `;

    // close button
    popup.querySelector("#wa-chat-close-btn").addEventListener("click", () => this._closePopup());

    // submit
    popup.querySelector("#wa-chat-submit").addEventListener("click", () => this._submitChat());

    // close on outside click
    document.addEventListener("click", (e) => {
      if (this._popupOpen && !popup.contains(e.target) && !this._btn.contains(e.target)) {
        this._closePopup();
      }
    });

    this._popup = popup;
    return popup;
  }

  _togglePopup() {
    if (this._popupOpen) this._closePopup();
    else                 this._openPopup();
  }

  _openPopup() {
    this._popup.classList.add("wa-open");
    this._popupOpen = true;
  }

  _closePopup() {
    this._popup.classList.remove("wa-open");
    this._popupOpen = false;
  }

  _submitChat() {
    const nameEl  = this._popup.querySelector("#wa-chat-name");
    const emailEl = this._popup.querySelector("#wa-chat-email");
    const msgEl   = this._popup.querySelector("#wa-chat-msg");
    const errName  = this._popup.querySelector("#wa-err-name");
    const errEmail = this._popup.querySelector("#wa-err-email");

    const name  = nameEl.value.trim();
    const email = emailEl.value.trim();
    const msg   = msgEl.value.trim();

    let valid = true;

    // validate name
    if (!name) {
      nameEl.classList.add("wa-error");
      errName.style.display = "block";
      valid = false;
    } else {
      nameEl.classList.remove("wa-error");
      errName.style.display = "none";
    }

    // validate email
    const emailOK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailOK) {
      emailEl.classList.add("wa-error");
      errEmail.style.display = "block";
      valid = false;
    } else {
      emailEl.classList.remove("wa-error");
      errEmail.style.display = "none";
    }

    if (!valid) return;

    // build WhatsApp message
    const page = document.title || window.location.href;
    let wa = `${this._lang.greeting} ${name} (${email}).`;
    if (msg) wa += `\n\n${msg}`;
    wa += `\n\n${this._lang.pageLine} ${page}`;

    const url = `https://wa.me/${this.config.phone}?text=${encodeURIComponent(wa)}`;
    if (this.config.newTab) window.open(url, "_blank", "noopener");
    else window.location.href = url;

    this._closePopup();
  }

  // ── INIT ─────────────────────────────────────────────────────────────────
  init() {
    const mount = () => {
      _waInjectCSS();
      const cfg = this.config;

      // pulse ring first (behind button)
      if (cfg.fx.pulse) document.body.appendChild(this._createPulse());

      // main button
      const btn = this._createBtn();
      document.body.appendChild(btn);

      // tooltip (after button so we can reference _btn)
      if (cfg.fx.tooltip) document.body.appendChild(this._createTooltip());

      // badge (appended inside button as child)
      if (cfg.fx.badge) btn.appendChild(this._createBadge());

      // chat popup
      if (cfg.chat.enabled) document.body.appendChild(this._createPopup());

      // keep pulse ring aligned with button
      if (cfg.fx.pulse && this._pulse) {
        const sync = () => {
          if (!this._btn) return;
          const r = this._btn.getBoundingClientRect();
          this._pulse.style.top  = `${r.top}px`;
          this._pulse.style.left = `${r.left}px`;
          // override the fixed positioning set via posCSS
          this._pulse.style.bottom = "auto";
          this._pulse.style.right  = "auto";
        };
        requestAnimationFrame(sync);
        window.addEventListener("resize", sync);
        window.addEventListener("scroll", sync);
      }
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", mount);
    } else {
      mount();
    }
    return this;
  }

  // ── DESTROY ──────────────────────────────────────────────────────────────
  destroy() {
    ["wa-float-btn","wa-pulse-ring","wa-tooltip","wa-chat-popup","wa-btn-css"]
      .forEach(id => document.getElementById(id)?.remove());
    this._btn = this._pulse = this._tooltip = this._badge = this._popup = null;
    this._popupOpen = false;
  }

  // ── UPDATE ───────────────────────────────────────────────────────────────
  update(newConfig = {}) {
    const cur = this.config;
    Object.assign(cur, newConfig);
    if (newConfig.fx)   Object.assign(cur.fx,   newConfig.fx);
    if (newConfig.chat) Object.assign(cur.chat,  newConfig.chat);
    this.destroy();
    this.init();
  }
}

// ─── AUTO-INIT ────────────────────────────────────────────────────────────────
(function () {
  function autoInit() {
    window.waBtn = new WhatsAppButton();
    window.waBtn.init();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit);
  } else {
    autoInit();
  }
})();
